/**
 * Fuentes de imagen para el portafolio.
 *
 * Sustituye a los helpers de data/fotografia.js, que construían URLs de
 * Unsplash. Aquí solo se sirven archivos propios desde public/fotografia/:
 * ninguna imagen del sitio sale a un CDN externo, lo que además saca a
 * images.unsplash.com de la ruta crítica de renderizado.
 *
 * El script scripts/fotos.mjs genera los WebP y escribe `src`, `srcSet`,
 * `w`, `h` y `ratio` en el manifiesto, así que aquí no hay que calcular nada:
 * solo elegir el `sizes` correcto según dónde se pinte la foto.
 */

/**
 * Valores de `sizes` por contexto de uso.
 *
 * `sizes` le dice al navegador qué ancho ocupará la imagen ANTES de conocer
 * el layout, así que si está mal, descarga la variante equivocada y el
 * srcSet no sirve de nada.
 */
export const SIZES = {
  /** Foto a sangre: ocupa el viewport completo. */
  full: "100vw",
  /** Rejilla de 3 columnas en desktop, 2 en tablet, 1 en móvil. */
  grid3: "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw",
  /** Rejilla de 4 columnas en desktop, 2 en móvil. */
  grid4: "(min-width: 768px) 25vw, 50vw",
  /** Rejilla de 2 columnas. */
  grid2: "(min-width: 640px) 50vw, 100vw",
  /** Mosaico del portafolio. */
  masonry: "(min-width: 1024px) 30vw, 46vw",
  /** Media página: hero de segmento, bloques de dos columnas. */
  half: "(min-width: 1024px) 50vw, 100vw",
};

/**
 * Devuelve lo necesario para pintar un `<img>`, o null si la foto aún no
 * existe (el manifiesto arranca vacío hasta que se suben las fotos reales).
 *
 * Nunca devuelve una URL externa: si no hay archivo propio, no hay imagen y
 * el componente pinta un marcador.
 */
export const photoSources = (foto, { sizes = SIZES.grid3 } = {}) => {
  if (!foto || !foto.src) return null;
  return {
    src: foto.src,
    srcSet: foto.srcSet || undefined,
    sizes: foto.srcSet ? sizes : undefined,
    width: foto.w,
    height: foto.h,
  };
};

/**
 * Relación de aspecto para reservar el espacio del contenedor.
 *
 * Se prefiere el ratio medido por ffprobe; el 0.75 (4:3) solo aplica a los
 * marcadores, donde no hay archivo del que medir nada.
 */
export const photoRatio = (foto, fallback = 0.75) =>
  foto && foto.ratio ? foto.ratio : fallback;
